import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { usersAPI, companiesAPI, rolesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MoreHorizontal, Shield, Building, Loader2, UserPlus, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

const Users = () => {
  const { isSuperAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', is_super_admin: false, company_assignments: []
  });
  
  const [assignData, setAssignData] = useState({ company_id: '', role_id: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, companiesRes, rolesRes] = await Promise.all([
        usersAPI.getAll(),
        companiesAPI.getAll(),
        rolesAPI.getAll()
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({ name: user.name, email: user.email, password: '', is_super_admin: user.is_super_admin || false, company_assignments: [] });
    } else {
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', is_super_admin: false, company_assignments: [] });
    }
    setIsDialogOpen(true);
  };

  const openAssignDialog = (user) => {
    setSelectedUser(user);
    setAssignData({ company_id: '', role_id: '' });
    setIsAssignOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingUser) {
        await usersAPI.update(editingUser.id, formData);
        toast.success('User updated');
      } else {
        await usersAPI.create(formData);
        toast.success('User created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await usersAPI.assignCompany(selectedUser.id, {
        user_id: selectedUser.id,
        company_id: assignData.company_id,
        role_id: assignData.role_id
      });
      toast.success('User assigned to company');
      setIsAssignOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveFromCompany = async (userId, companyId, companyName) => {
    if (window.confirm(`Remove user from ${companyName}?`)) {
      try {
        await usersAPI.removeFromCompany(userId, companyId);
        toast.success('User removed from company');
        fetchData();
      } catch (error) {
        toast.error('Failed to remove');
      }
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error("Cannot delete yourself");
      return;
    }
    if (window.confirm('Delete this user?')) {
      try {
        await usersAPI.delete(id);
        toast.success('User deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-500 mt-1">Manage users and company assignments</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-teal-700 hover:bg-teal-800" data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />Add User
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">User</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Email</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Role</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Companies</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow key={user.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{user.name}</span>
                      {user.is_super_admin && (
                        <Badge className="bg-purple-100 text-purple-700"><Shield className="h-3 w-3 mr-1" />Super Admin</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{user.email}</TableCell>
                  <TableCell>
                    {user.is_super_admin ? (
                      <Badge className="bg-purple-100 text-purple-700">All Access</Badge>
                    ) : (
                      <span className="text-slate-500">Per Company</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {user.companies?.map(c => (
                        <Badge key={c.company?.id} variant="outline" className="text-xs cursor-pointer hover:bg-red-50"
                          onClick={() => handleRemoveFromCompany(user.id, c.company?.id, c.company?.name)}>
                          {c.company?.code}: {c.role?.name}
                        </Badge>
                      ))}
                      {(!user.companies || user.companies.length === 0) && !user.is_super_admin && (
                        <span className="text-slate-400 text-sm">No assignments</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openAssignDialog(user)}>
                          <UserPlus className="h-4 w-4 mr-2" />Assign to Company
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openDialog(user)}>
                          <Edit className="h-4 w-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(user.id)} className="text-red-600" disabled={user.id === currentUser?.id}>
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              </div>
              <div className="grid gap-2">
                <Label>Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
              </div>
              {!editingUser && (
                <div className="grid gap-2">
                  <Label>Password *</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required={!editingUser} />
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <Label>Super Admin</Label>
                  <p className="text-xs text-slate-500">Full access to all companies</p>
                </div>
                <Switch checked={formData.is_super_admin} onCheckedChange={(val) => setFormData({...formData, is_super_admin: val})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign to Company Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign to Company</DialogTitle>
            <DialogDescription>Assign {selectedUser?.name} to a company with a specific role</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssign}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Company *</Label>
                <Select value={assignData.company_id} onValueChange={(val) => setAssignData({...assignData, company_id: val})}>
                  <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Role *</Label>
                <Select value={assignData.role_id} onValueChange={(val) => setAssignData({...assignData, role_id: val})}>
                  <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAssignOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting || !assignData.company_id || !assignData.role_id}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Assign
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
